import React, { useEffect, useMemo, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import connect from '../../utils/connect';
import combineActions from '../../utils/combineActions';

import sportsApi from '../../utils/sportsApi';

import { Card, Row, Col, LoadingDots } from '@app/ui';
import AccordionTitle from '@app/ui/sports-components/AccordionTitle';
import AccordionIcon from '@app/ui/sports-components/AccordionIcon';
import AccordionChildItems from '@app/ui/sports-components/AccordionChildItems';
import AccordionItem from '@app/ui/sports-components/AccordionItem';
import classNames from '@app/ui/sports-components/utils/classNames';
import removePlacementImage from '../../actions/settings/removePlacementImage';
import Tooltip from '@app/ui/sports-components/Tooltip';

import FileUpload from '../FileUpload';
import { useDroppable } from '@dnd-kit/core';
import { RIGHTSHOLDER_PLACEMENT_EXAMPLE } from '../../utils/s3Config';
import { Context } from '../contexts/OnboardingContext';
import { PlacementContext } from '../contexts/PlacementContext';
import { getSponsorActiveAtPlacement } from '../../utils/getActiveOnboardingInstances';
import getCount from '../../utils/getCount';
import { STANDARD_PLACEMENT_EXPERIENCE } from '../../constants/config';
import { fetchPlacementTypeExamples } from '../api/placementTypeExamples';

const SinglePlacement = ({ removePlacementImage, client }) => {
    const {
        rhId,
        structureLocked,
        canAddPlacementSponsors,
        hideInactiveAssets,
        isDraggableActive: showSponsorUploader,
        setDraggableActive: setShowSponsorUploader,
        currentSelectedPlacement,
        isDragging,
        sponsorsToAdd,
        setSponsorsToAdd,
        sponsorsToRemove,
        setSponsorsToRemove,
        sportId,
        leagueId,
        setIsSelectingAllSponsors,
    } = useContext(Context);

    const {
        setPlacementTypesLoading,
        files,
        setFiles,
        prevFiles,
        setPrevFiles,
        setShowModal,
        setPlacementExampleId,
        setPlacementTypeOptions,
        reloImages,
        setReloImages,
        reloImagesLoading,
        setReloImagesLoading,
        setShowReloImages,
        setReloImageId,
    } = useContext(PlacementContext);

    const [isExpanded, setIsExpanded] = useState(false);
    const { isOver, setNodeRef } = useDroppable({
        id: 'droppable_placements',
    });

    const clientName = client.name;
    const showStandardPlacements = client.permissions.includes(
        STANDARD_PLACEMENT_EXPERIENCE,
    );

    const associatedSponsors = useMemo(() => {
        const selectedSponsors =
            currentSelectedPlacement && currentSelectedPlacement.sponsors;

        if (!selectedSponsors) return [];
        const activeSponsors = selectedSponsors
            ? selectedSponsors
                  .filter(s => getSponsorActiveAtPlacement(s))
                  .sort((a, b) => a.name.localeCompare(b.name))
            : [];
        const nonActiveSponsors = selectedSponsors
            ? selectedSponsors
                  .filter(s => !getSponsorActiveAtPlacement(s))
                  .sort((a, b) => a.name.localeCompare(b.name))
            : [];
        return [...activeSponsors, ...nonActiveSponsors];
    }, [currentSelectedPlacement]);

    const [filesToShow, setFilesToShow] = useState([]);

    useEffect(() => {
        const newFilesToShow = [...prevFiles, ...files].reduce((acc, f) => {
            const ex =
                currentSelectedPlacement &&
                currentSelectedPlacement.examples &&
                currentSelectedPlacement.examples.find(e => e.id === f.id);
            let newFile = f;
            if (ex && ex.boundingBoxes) {
                newFile = {
                    ...newFile,
                    boundingBoxes: ex.boundingBoxes,
                };
            }
            if (f.imageUrl) {
                newFile = {
                    ...newFile,
                    url: f.imageUrl,
                };
            }
            acc.push(newFile);
            return acc;
        }, []);
        setFilesToShow(newFilesToShow);
        if (newFilesToShow.length === 0) {
            setIsExpanded(true);
        }
    }, [files, prevFiles, currentSelectedPlacement]);

    useEffect(() => {
        if (!rhId) return;
        if (showStandardPlacements && !leagueId) return;
        if (!showStandardPlacements && !sportId) return;
        let cancelled = false;

        (async () => {
            setPlacementTypesLoading(true);
            try {
                let endPoint = `/placement-types`;
                if (showStandardPlacements) {
                    endPoint = `/leagues/${leagueId}/placement-types`;
                } else if (Number.isInteger(sportId)) {
                    endPoint += `?sportId=${sportId}`;
                }

                const types = await sportsApi.url(endPoint).get().json();
                if (cancelled) return;

                const options = types
                    .map(s => ({
                        value: s.id,
                        name: s.name,
                    }))
                    .sort((a, b) => {
                        return a.name?.localeCompare(b.name);
                    });
                setPlacementTypeOptions(options);
            } catch (e) {
                // eslint-disable-next-line no-console
                console.log(e);
            } finally {
                if (!cancelled) setPlacementTypesLoading(false);
            }
        })();

        return () => {
            cancelled = true;
            setFiles([]);
        };
    }, [rhId, showStandardPlacements, leagueId]);

    useEffect(() => {
        (async () => {
            setReloImagesLoading(true);
            try {
                if (!showStandardPlacements || !leagueId) return;
                const { placementType } = currentSelectedPlacement;
                if (!placementType) return;
                const { id: placementTypeId } = placementType;
                const examples = await fetchPlacementTypeExamples(
                    leagueId,
                    placementTypeId,
                );
                setReloImages(examples);
            } finally {
                setReloImagesLoading(false);
            }
        })();
        return () => setReloImages([]);
    }, [currentSelectedPlacement, leagueId, showStandardPlacements]);

    const currentPlacementId = currentSelectedPlacement
        ? currentSelectedPlacement.id
        : null;

    useEffect(() => {
        setSponsorsToAdd([]);
        setSponsorsToRemove([]);
    }, [currentPlacementId]);

    const toggleSponsorButton = () => {
        if (!canAddPlacementSponsors) return;
        if (showSponsorUploader) {
            setSponsorsToAdd([]);
        }
        setIsSelectingAllSponsors(false);
        setShowSponsorUploader(!showSponsorUploader);
    };

    const sponsorButtonClasses = classNames(
        'gds-button',
        'gds-button--xs',
        '-p-h-2',
        '-m-b-0',
        {
            'gds-button--secondary': !showSponsorUploader,
            'gds-button--default': showSponsorUploader,
        },
    );

    const removeSp = sp => async () => {
        try {
            if (structureLocked) return;
            const { isNew = false } = sp;
            if (!isNew) {
                const confirmation = confirm(
                    `Warning: You are about to delete ${sp.name} from ${currentSelectedPlacement.name} which means it will no longer be tracked.`,
                );
                if (!confirmation) return;
            }
            setSponsorsToRemove([...sponsorsToRemove, sp]);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.log(e);
        }
    };

    const sponsorsToShow = sponsorsToAdd.reduce((acc, sp) => {
        if (
            (sponsorsToRemove.findIndex(s => s.id === sp.id) < 0 ||
                getCount({
                    el: sp,
                    arr: sponsorsToRemove,
                }) <
                    getCount({
                        el: sp,
                        arr: sponsorsToAdd,
                    })) &&
            acc.findIndex(s => s.id === sp.id) < 0
        ) {
            acc.push(sp);
        }
        return acc;
    }, []);

    const associatedSponsorsToShow = [
        ...associatedSponsors,
        ...sponsorsToShow,
    ].reduce((acc, sp) => {
        if (
            sponsorsToRemove.findIndex(s => s.id == sp.id) > -1 &&
            getCount({
                el: sp,
                arr: sponsorsToRemove,
            }) >=
                getCount({
                    el: sp,
                    arr: sponsorsToAdd,
                })
        ) {
            acc.push({ ...sp, toDelete: true });
        } else {
            acc.push(sp);
        }
        return acc;
    }, []);

    const orderedAssociatedSponsorsToShow = [
        ...associatedSponsorsToShow.filter(sp =>
            getSponsorActiveAtPlacement(sp),
        ),
        ...(hideInactiveAssets
            ? []
            : associatedSponsorsToShow.filter(
                  sp => !getSponsorActiveAtPlacement(sp),
              )),
    ];

    const hasCurrentSelectorSponsors =
        orderedAssociatedSponsorsToShow.length > 0;

    useEffect(() => {
        if (
            currentSelectedPlacement &&
            !hasCurrentSelectorSponsors &&
            canAddPlacementSponsors
        ) {
            setShowSponsorUploader(true);
        }
    }, [
        currentSelectedPlacement,
        hasCurrentSelectorSponsors,
        canAddPlacementSponsors,
    ]);

    const deleteImage = f => async e => {
        e.stopPropagation();
        if (structureLocked) return;
        const confirmation = confirm(
            'Are you sure you want to remove this image?',
        );
        if (!confirmation) return;

        const { isNew, id } = f;
        try {
            if (!isNew) {
                await removePlacementImage({
                    rhId,
                    plId: currentPlacementId,
                    example: f,
                });

                const endPoint = `/clients/${client.id}/rightsholders/${rhId}/placements/${currentPlacementId}/examples/${id}`;
                await sportsApi.url(endPoint).delete().res();

                const newPrevFiles = prevFiles.filter(file => file.id != id);
                setPrevFiles(newPrevFiles);
            } else {
                const newFiles = files.filter(file => file.id != id);
                setFiles(newFiles);
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.log(err);
        }
    };

    const triggerCollapse = () => {
        setIsExpanded(!isExpanded);
    };

    return (
        <>
            <Row
                gutter={16}
                style={{
                    overflow: 'auto',
                }}
            >
                <Col md={24} className="-m-b-2">
                    <div
                        style={
                            structureLocked
                                ? {
                                      pointerEvents: 'none',
                                      opacity: 0.55,
                                  }
                                : undefined
                        }
                    >
                        <FileUpload
                            {...{
                                uploadType: RIGHTSHOLDER_PLACEMENT_EXAMPLE,
                                setFiles,
                                files,
                                metadata: { rhId },
                                isReady: uploads =>
                                    files.filter(f => f.justAdded).length ===
                                    uploads.length,
                            }}
                        />
                    </div>
                </Col>
            </Row>
            <Row gutter={16}>
                <Col md={24} className="-m-b-2">
                    {filesToShow.length > 0 && (
                        <>
                            <div className="-m-v-2">
                                <label className="gds-form-group__label -m-b-0 -p-l-2">
                                    {clientName} Images
                                </label>
                            </div>

                            <Row gutter={16}>
                                {filesToShow.map(f => {
                                    const { id, url } = f;
                                    const onClick = () => {
                                        setShowModal(true);
                                        setPlacementExampleId(id);
                                    };
                                    const hasBB =
                                        Array.isArray(f.boundingBoxes) &&
                                        f.boundingBoxes.length > 0;
                                    return (
                                        <Col key={id} md={8} className="-m-b-2">
                                            <li
                                                className="gds-ranker-card__item gds-placement-image gds-flex--justify-center -m-b-2-lg -cursor--pointer"
                                                onClick={onClick}
                                            >
                                                <img
                                                    className="gds-image"
                                                    src={url}
                                                />
                                                <div className="gds-ranker-card__data-wrapper">
                                                    {!hasBB && (
                                                        <div className="-color-bg-war">
                                                            <p className="gds-text--body-sm gds-text--regular -color-tx-white -p-a-1">
                                                                <i className="fas fa-exclamation-triangle -m-h-1" />
                                                                Bounding box
                                                            </p>
                                                        </div>
                                                    )}

                                                    <div className="gds-ranker-card__footer -text-right -z-2">
                                                        <button
                                                            type="button"
                                                            className="gds-circular-button -m-r-2 -m-b-1 gds-circular-button--xs gds-circular-button--danger"
                                                            onClick={deleteImage(
                                                                f,
                                                            )}
                                                            disabled={
                                                                structureLocked
                                                            }
                                                        >
                                                            <i className="fa fa-fw fa-trash-alt" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </li>
                                        </Col>
                                    );
                                })}
                            </Row>
                        </>
                    )}
                </Col>
            </Row>

            {showStandardPlacements && (
                <>
                    <Row gutter={16}>
                        <Col md={24} className="-m-v-2">
                            <AccordionItem
                                data-test="relo-images-container"
                                isActive={isExpanded}
                                className="relo-images"
                            >
                                <AccordionTitle
                                    className="relo-images-title -color-bg-white -p-b-0"
                                    size="sm"
                                    onClick={triggerCollapse}
                                    data-test="collapse-relo-images"
                                >
                                    <div className="gds-flex gds-flex--align-center gds-flex--justify-between -m-v-2">
                                        <div className="gds-flex__item gds-flex__item--grow-0">
                                            <label className="gds-form-group__label -m-b-0">
                                                Relo Images
                                            </label>
                                        </div>
                                    </div>
                                </AccordionTitle>
                                <AccordionIcon
                                    size="sm"
                                    className="relo-images-icon --cursor-pointer"
                                    onClick={triggerCollapse}
                                />
                                {isExpanded && (
                                    <AccordionChildItems
                                        className="-p-h-3 -p-v-3"
                                        data-test="relo-images"
                                    >
                                        {reloImagesLoading && (
                                            <div
                                                className="gds-flex gds-flex--justify-center gds-flex--align-center"
                                                style={{
                                                    minHeight: '120px',
                                                    width: '100%',
                                                }}
                                            >
                                                <LoadingDots size="lg" />
                                            </div>
                                        )}
                                        {!reloImagesLoading &&
                                            reloImages.length > 0 && (
                                                <Row gutter={16}>
                                                    {reloImages.map(f => {
                                                        const {
                                                            id,
                                                            imageUrl: url,
                                                        } = f;
                                                        const onClickReloImg =
                                                            () => {
                                                                setShowModal(
                                                                    true,
                                                                );
                                                                setShowReloImages(
                                                                    true,
                                                                );
                                                                setReloImageId(
                                                                    id,
                                                                );
                                                            };
                                                        return (
                                                            <Col
                                                                key={id}
                                                                md={8}
                                                                className="-m-b-2"
                                                            >
                                                                <li
                                                                    className="gds-ranker-card__item gds-placement-image gds-flex--justify-center -m-b-2-lg -cursor--pointer"
                                                                    onClick={
                                                                        onClickReloImg
                                                                    }
                                                                >
                                                                    <img
                                                                        className="gds-image"
                                                                        src={
                                                                            url
                                                                        }
                                                                    />
                                                                </li>
                                                            </Col>
                                                        );
                                                    })}
                                                </Row>
                                            )}
                                    </AccordionChildItems>
                                )}
                            </AccordionItem>
                        </Col>
                    </Row>
                </>
            )}
            <div className="gds-flex gds-flex--align-center gds-flex--justify-between -m-v-2">
                <div className="gds-flex__item gds-flex__item--grow-0">
                    <label className="gds-form-group__label -m-b-0">
                        Sponsors
                    </label>
                </div>

                <div className="gds-flex__item gds-flex__item--grow-0">
                    <button
                        type="button"
                        className={sponsorButtonClasses}
                        onClick={toggleSponsorButton}
                        disabled={!canAddPlacementSponsors}
                    >
                        {!showSponsorUploader && (
                            <i className="fas fa-fw fa-plus-circle" />
                        )}
                        {!showSponsorUploader ? 'Add Sponsors' : 'Cancel'}
                    </button>
                </div>
            </div>
            <Row gutter={16}>
                {showSponsorUploader && (
                    <Col md={24} className="-m-b-2">
                        <div
                            ref={setNodeRef}
                            className="-p-a-2"
                            style={{
                                height: '10rem',
                                border: '1px dashed #c4c4c4',
                                backgroundColor:
                                    canAddPlacementSponsors &&
                                    isOver &&
                                    isDragging
                                        ? '#EDFAFD'
                                        : null,
                                cursor:
                                    canAddPlacementSponsors &&
                                    isOver &&
                                    isDragging
                                        ? 'copy'
                                        : null,
                                pointerEvents: !canAddPlacementSponsors
                                    ? 'none'
                                    : undefined,
                                opacity: !canAddPlacementSponsors
                                    ? 0.55
                                    : undefined,
                            }}
                            id="droppableContainer_placements"
                        >
                            {canAddPlacementSponsors &&
                                !(isOver && isDragging) && (
                                    <p className="gds-text-drag-sponsors gds-text--body-md ">
                                        Drag and drop sponsors from the right
                                    </p>
                                )}

                            {canAddPlacementSponsors &&
                                isOver &&
                                isDragging && (
                                    <p className="gds-text-drag-sponsors gds-text--body-md ">
                                        Add Sponsors here.
                                    </p>
                                )}
                        </div>
                    </Col>
                )}

                {hasCurrentSelectorSponsors && (
                    <Col md={24}>
                        <Row gutter={16}>
                            {orderedAssociatedSponsorsToShow.map(
                                (sp, index) => {
                                    const { logos, toDelete = false } = sp;
                                    const hasLogo = logos && logos.length > 0;
                                    const imgUrl = hasLogo
                                        ? logos[0].url
                                        : null;
                                    const cardClass = classNames(
                                        'gds-flex',
                                        'gds-flex--align-center',
                                        'gds-text--body-sm',
                                        '-p-a-2',
                                        {
                                            '-color-bg-lt-3':
                                                !getSponsorActiveAtPlacement(
                                                    sp,
                                                ),
                                            'zebra-striped': toDelete,
                                        },
                                    );

                                    const getTooltip = () => {
                                        if (!getSponsorActiveAtPlacement(sp))
                                            return 'This sponsor is deactivated and will no\nlonger be tracked. Please reach out to\nyour CSM to reactivate.';
                                        if (toDelete)
                                            return 'This sponsor is pending removal. Click Save to continue.';
                                        return '';
                                    };

                                    return (
                                        <Col
                                            md={8}
                                            key={`${sp.name}_selected_${sp.id}_${index}`}
                                        >
                                            <div className="gds-sponsor">
                                                <Tooltip
                                                    text={getTooltip()}
                                                    position="top"
                                                    style={{
                                                        width: '100%',
                                                    }}
                                                >
                                                    <Card>
                                                        <div
                                                            className={
                                                                cardClass
                                                            }
                                                        >
                                                            <div className="gds-sponsors__image -m-l-1 -m-r-2 -color-bg-white -cursor--pointer">
                                                                {hasLogo && (
                                                                    <img
                                                                        src={
                                                                            imgUrl
                                                                        }
                                                                        height="100%"
                                                                        width="100%"
                                                                    />
                                                                )}
                                                                {!hasLogo && (
                                                                    <div className="gds-flex gds-flex--justify-center gds-flex--content-center gds-flex-grid__item--full-height">
                                                                        <i className="fa fa-fw fa-exclamation-triangle gds-text--danger" />
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="gds-sponsor__name">
                                                                <p className="gds-text--body-sm gds-text--regular -ellipsis -p-r-1">
                                                                    {sp.name}
                                                                </p>
                                                            </div>
                                                            {getSponsorActiveAtPlacement(
                                                                sp,
                                                            ) &&
                                                                !toDelete && (
                                                                    <i
                                                                        className={classNames(
                                                                            'fa fa-fw fa-times-circle gds-text--body-md -color-tx-dk-5',
                                                                            {
                                                                                '-cursor--pointer':
                                                                                    !structureLocked,
                                                                                '-cursor--not-allowed':
                                                                                    structureLocked,
                                                                            },
                                                                        )}
                                                                        style={
                                                                            structureLocked
                                                                                ? {
                                                                                      opacity: 0.35,
                                                                                  }
                                                                                : undefined
                                                                        }
                                                                        onClick={
                                                                            structureLocked
                                                                                ? undefined
                                                                                : removeSp(
                                                                                      sp,
                                                                                  )
                                                                        }
                                                                        role="button"
                                                                        aria-disabled={
                                                                            structureLocked
                                                                        }
                                                                    />
                                                                )}
                                                        </div>
                                                    </Card>
                                                </Tooltip>
                                            </div>
                                        </Col>
                                    );
                                },
                            )}
                        </Row>
                    </Col>
                )}
            </Row>
        </>
    );
};

SinglePlacement.propTypes = {
    removePlacementImage: PropTypes.func,
    client: PropTypes.object,
};

export default connect(
    ['user', 'client'],
    combineActions({
        removePlacementImage,
    }),
)(SinglePlacement);
